import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["form"];
  static values = {
    date: String,
    bookableType: String,
    agendaStartHour: Number,
  };

  connect() {
    this.draggedAppointment = null;
    this.draggedDuration = 0;
    this.lastValidSlotIndex = null;
    this.originalVisualState = null;
    this.mouseY = 0; // Stocker la position Y de la souris
    this.isDragging = false; // Track si on est en train de draguer

    // Écouter les mouvements de souris pour capturer la position
    this.mouseMoveHandler = (e) => {
      this.mouseY = e.clientY;

      // Si on est en train de draguer, mettre à jour le highlighting
      if (this.isDragging) {
        this.updateHighlightingDuringDrag(e);
      }
    };

    document.addEventListener('mousemove', this.mouseMoveHandler);

    // Attendre que le DOM soit complètement chargé
    this.setupSortable();
  }

  disconnect() {
    // Nettoyer l'event listener quand le controller est déconnecté
    if (this.mouseMoveHandler) {
      document.removeEventListener('mousemove', this.mouseMoveHandler);
    }
  }

  setupSortable() {
    // Trouver tous les conteneurs d'appointments (ol avec id appointments_for_bookable_*)
    const appointmentContainers = this.element.querySelectorAll('ol[id^="appointments_for_bookable_"]');

    appointmentContainers.forEach(container => {
      // Configurer SortableJS pour chaque conteneur d'appointments
      new Sortable(container, {
        group: 'schedule',
        animation: 150,
        ghostClass: 'opacity-50',
        chosenClass: 'scale-105',
        dragClass: 'rotate-2',
        onChoose: this.onChoose.bind(this),
        onStart: this.onStart.bind(this),
        onMove: this.onMove.bind(this),
        onEnd: this.onEnd.bind(this),
        filter: ".time-slot, .drag-create-link", // Ne pas permettre de déplacer les slots vides et liens
        preventOnFilter: false,
        sort: true,
        forceFallback: true,
        fallbackTolerance: 5
      });
    });
  }

  onChoose(evt) {
    const item = evt.item;

    // Sauvegarder l'état visuel complet AVANT toute modification par SortableJS
    this.originalVisualState = {
      parent: item.parentNode,
      nextSibling: item.nextElementSibling,
      elementHTML: item.outerHTML, // Sauvegarder le HTML complet de l'élément
      elementId: item.id
    };

    console.log('Saved original state before SortableJS modifications');
  }

  onStart(evt) {
    const item = evt.item;
    this.draggedAppointment = item;
    this.draggedDuration = parseInt(item.dataset.appointmentDragDurationValue) || 20;
    this.isDragging = true; // Commencer le tracking du drag

    // Ajouter une classe au body pour désactiver les liens de création
    document.body.classList.add('sortable-dragging');

    // Modifier l'opacité pour le drag
    evt.item.style.opacity = '0';

    console.log(`Starting drag for appointment with duration: ${this.draggedDuration} minutes`);
  }

  onMove(evt) {
    const target = evt.related;
    const targetColumn = evt.to; // C'est le conteneur d'appointments (ol)

    console.log('=== onMove Debug ===');
    console.log('target:', target);
    console.log('targetColumn:', targetColumn);

    // Trouver la colonne parente (div avec data-bookable-line)
    const columnContainer = targetColumn.closest('[data-bookable-line]');
    if (!columnContainer) {
      console.log('No column container found');
      return false;
    }

    // Vérifier si la colonne de destination est en congé
    if (columnContainer && columnContainer.dataset.onVacation === 'true') {
      this.lastValidSlotIndex = null; // S'assurer que lastValidSlotIndex est null
      return false;
    }

    // Obtenir l'ID de la ressource depuis le conteneur de colonnes
    const slotsContainer = columnContainer.querySelector('[data-schedule-drag-drop-target="column"]');
    if (!slotsContainer) {
      console.log('No slots container found');
      return false;
    }

    const bookableId = slotsContainer.dataset.bookableId;

    // Utiliser la position de la souris pour calculer l'index du slot
    // C'est plus fiable que d'essayer de deviner à partir du target
    const rect = slotsContainer.getBoundingClientRect();
    const relativeY = this.mouseY - rect.top;
    const slotHeight = 1.2 * 16; // 1.2rem convertie en pixels
    let targetIndex = Math.floor(Math.max(0, relativeY) / slotHeight);

    // Vérifier que l'index calculé existe dans les slots
    const timeSlots = Array.from(slotsContainer.querySelectorAll('.time-slot'));
    if (timeSlots.length === 0) {
      console.log('No time slots found');
      return false;
    }

    // S'assurer que l'index est dans les limites
    targetIndex = Math.max(0, Math.min(targetIndex, timeSlots.length - 1));

    // Vérifier qu'il existe un slot avec cet index
    let targetSlot = timeSlots.find(s => parseInt(s.dataset.slotIndex) === targetIndex);
    if (!targetSlot) {
      // Fallback: prendre le slot à la position dans le tableau
      targetSlot = timeSlots[targetIndex];
      if (targetSlot) {
        targetIndex = parseInt(targetSlot.dataset.slotIndex);
      }
    }

    console.log(`Mouse calc: Y=${this.mouseY}, containerTop=${rect.top}, relativeY=${relativeY}, slotHeight=${slotHeight}`);
    console.log(`Calculated targetIndex: ${targetIndex}, slot found:`, targetSlot);
    console.log(`Slot data-slot-index: ${targetSlot?.dataset?.slotIndex}`);

    if (!targetSlot || isNaN(targetIndex)) {
      console.log('No valid target slot found');
      return false;
    }

    this.clearAllHighlights();

    const slotsNeeded = Math.ceil(this.draggedDuration / 5);

    const canDrop = this.canDropAtPosition(slotsContainer, targetIndex, slotsNeeded, bookableId);

    if (canDrop) {
      // Stocker l'index du slot survolé SEULEMENT si le drop est autorisé
      this.lastValidSlotIndex = targetIndex;
      this.highlightSlots(slotsContainer, targetIndex, slotsNeeded, 'bg-green-100 border-green-300');
      return true;
    } else {
      // Effacer l'index valide si le drop n'est pas autorisé
      this.lastValidSlotIndex = null;
      this.highlightSlots(slotsContainer, targetIndex, slotsNeeded, 'bg-red-100 border-red-300');
      return false;
    }
  }

  canDropAtPosition(column, startIndex, slotsNeeded, bookableId = null) {
    const timeSlots = Array.from(column.querySelectorAll('.time-slot'));

    // Vérifier qu'il y a assez de slots en cherchant par data-slot-index
    for (let i = 0; i < slotsNeeded; i++) {
      const targetSlotIndex = startIndex + i;
      const slot = timeSlots.find(s => parseInt(s.dataset.slotIndex) === targetSlotIndex);
      if (!slot) {
        return false;
      }

      // Vérifier si le slot est désactivé via l'attribut data-disabled
      if (slot.dataset.disabled === 'true') {
        return false;
      }
    }

    // Utiliser le bookableId passé en paramètre ou le récupérer de la colonne
    const finalBookableId = bookableId || column.closest('[data-bookable-id]')?.dataset.bookableId;
    if (finalBookableId) {
      return this.checkAppointmentConflicts(finalBookableId, startIndex, slotsNeeded);
    }

    return true;
  }

  checkAppointmentConflicts(bookableId, startSlotIndex, slotsNeeded) {
    // Trouver le container des rendez-vous pour cette ressource
    const appointmentsContainer = document.getElementById(`appointments_for_bookable_${bookableId}`);
    if (!appointmentsContainer) return true;

    // Calculer la plage de temps que le nouveau rendez-vous occuperait
    const newStartTime = this.calculateTimeFromIndex(startSlotIndex);
    const newEndSlotIndex = startSlotIndex + slotsNeeded;
    const newEndTime = this.calculateTimeFromIndex(newEndSlotIndex);

    // Parcourir tous les rendez-vous existants dans cette colonne
    const existingAppointments = appointmentsContainer.querySelectorAll('[data-appointment-drag-id-value]');

    for (const appointment of existingAppointments) {
      // Ignorer le rendez-vous qu'on est en train de déplacer
      if (appointment === this.draggedAppointment) continue;

      // Extraire les informations du rendez-vous existant
      const appointmentDuration = parseInt(appointment.dataset.appointmentDragDurationValue) || 20;
      const appointmentSlots = Math.ceil(appointmentDuration / 5);

      // Calculer la position du rendez-vous existant en fonction de son style top
      const appointmentTop = appointment.style.top;
      if (!appointmentTop) continue;

      const topRem = parseFloat(appointmentTop.replace('rem', ''));
      // Conversion de rem en index de slot : (topRem - offset) / slot_height
      // L'offset est de 7px (0.4375rem) + 1 slot (1.2rem) = 1.6375rem
      // Chaque slot fait 1.2rem
      const existingSlotIndex = Math.round((topRem - (7 / 16 + 1.2)) / 1.2);
      const existingEndSlotIndex = existingSlotIndex + appointmentSlots;

      // Vérifier le chevauchement
      const hasOverlap = !(newEndSlotIndex <= existingSlotIndex || startSlotIndex >= existingEndSlotIndex);

      if (hasOverlap) {
        console.log(`Conflict detected: new slot ${startSlotIndex}-${newEndSlotIndex} overlaps with existing slot ${existingSlotIndex}-${existingEndSlotIndex}`);
        return false;
      }
    }

    return true;
  }

  highlightSlots(column, startIndex, count, cssClasses) {
    const timeSlots = Array.from(column.querySelectorAll('.time-slot'));

    console.log(`Highlighting slots: startIndex=${startIndex}, count=${count}, totalSlots=${timeSlots.length}`);

    for (let i = 0; i < count; i++) {
      // Chercher le slot par son data-slot-index au lieu de sa position dans le tableau
      const targetSlotIndex = startIndex + i;
      const slot = timeSlots.find(s => parseInt(s.dataset.slotIndex) === targetSlotIndex);

      if (slot) {
        console.log(`Highlighting slot with data-slot-index=${slot.dataset.slotIndex}`);
        slot.classList.add(...cssClasses.split(' '));
      } else {
        console.log(`No slot found with data-slot-index=${targetSlotIndex}`);
      }
    }
  }

  clearAllHighlights() {
    const highlightClasses = ['bg-green-100', 'border-green-300', 'bg-red-100', 'border-red-300'];
    this.element.querySelectorAll('.time-slot').forEach(slot => {
      slot.classList.remove(...highlightClasses);
    });
  }

  onEnd(event) {
    const { item, to, from, oldIndex, newIndex } = event;

    // Arrêter le tracking du drag
    this.isDragging = false;

    // Retirer la classe du body
    document.body.classList.remove('sortable-dragging');

    this.clearAllHighlights();
    item.style.opacity = '';

    const appointmentId = item.dataset.appointmentDragIdValue;
    if (!appointmentId) {
      console.error("Dragged item has no appointment ID.");
      return;
    }

    // Calculer la nouvelle position
    let newBookableId, timeStr;

    // Le conteneur 'to' est le conteneur d'appointments (ol)
    // Trouver la colonne parente pour obtenir le bookableId
    const columnContainer = to.closest('[data-bookable-line]');
    if (!columnContainer) {
      console.error("Cannot find column container.");
      this.restoreOriginalVisualState();
      return;
    }

    const slotsContainer = columnContainer.querySelector('[data-schedule-drag-drop-target="column"]');
    if (!slotsContainer) {
      console.error("Cannot find slots container.");
      this.restoreOriginalVisualState();
      return;
    }

    newBookableId = slotsContainer.dataset.bookableId;

    if (!newBookableId) {
      console.error("Target column has no bookable ID.");
      this.restoreOriginalVisualState();
      return;
    }

    // Utiliser l'index capturé pendant onMove
    let slotIndex = this.lastValidSlotIndex;
    if (slotIndex === null || slotIndex === undefined) {
      this.restoreOriginalVisualState();
      this.draggedAppointment = null;
      this.draggedDuration = 0;
      this.lastValidSlotIndex = null;
      return;
    }

    timeStr = this.calculateTimeFromIndex(slotIndex);
    console.log(`Drop completed: bookableId=${newBookableId}, slotIndex=${slotIndex}, time=${timeStr}`);

    // VALIDATION FINALE : vérifier que la position de drop est valide
    const slotsNeeded = Math.ceil(this.draggedDuration / 5);
    const canDrop = this.canDropAtPosition(slotsContainer, slotIndex, slotsNeeded, newBookableId);

    if (!canDrop) {
      console.log(`Drop rejected: position not valid for bookable ${newBookableId} at slot ${slotIndex}`);
      this.restoreOriginalVisualState();
      this.draggedAppointment = null;
      this.draggedDuration = 0;
      this.lastValidSlotIndex = null;
      return;
    }

    // Supprimer immédiatement l'élément - le serveur gérera la restauration via broadcast si nécessaire
    item.remove();

    this.moveAppointment(appointmentId, newBookableId, timeStr);

    this.draggedAppointment = null;
    this.draggedDuration = 0;
    this.lastValidSlotIndex = null;
  }

  calculateTimeFromIndex(slotIndex) {
    console.log(`Calculating time for slot index: ${slotIndex}`);

    // Récupérer l'heure de début de l'agenda depuis les values Stimulus
    const agendaStartHour = this.agendaStartHourValue || 8;

    // Soustraire 1 slot pour corriger le décalage ajouté dans _agenda_slot.html.erb
    const adjustedSlotIndex = slotIndex;

    // Chaque slot représente 5 minutes
    const minutesFromStart = adjustedSlotIndex * 5;
    const hours = Math.floor(minutesFromStart / 60) + agendaStartHour;
    const minutes = minutesFromStart % 60;

    const validHours = Math.max(agendaStartHour, Math.min(23, hours));
    const validMinutes = Math.max(0, Math.min(55, minutes));

    console.log(`Slot ${slotIndex} -> adjusted slot ${adjustedSlotIndex} -> ${validHours}:${validMinutes.toString().padStart(2, '0')}`);

    return `${validHours.toString().padStart(2, '0')}${validMinutes.toString().padStart(2, '0')}`;
  }

  moveAppointment(appointmentId, newBookableId, timeStr) {
    const form = this.formTarget;
    form.action = `/appointments/${appointmentId}/move`;

    const bookableIdInput = form.querySelector('input[name="appointment[bookable_id]"]');
    const timeStrInput = form.querySelector('input[name="appointment[time_str]"]');

    if (bookableIdInput && timeStrInput) {
      bookableIdInput.value = newBookableId;
      timeStrInput.value = timeStr;

      // Utiliser fetch pour pouvoir gérer les erreurs et restaurer si nécessaire
      const formData = new FormData(form);

      fetch(form.action, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'text/vnd.turbo-stream.html, application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      })
        .then(async response => {
          if (response.ok) {
            // Succès : nettoyer l'état sauvegardé
            this.originalVisualState = null;
            console.log('Appointment moved successfully');
          } else {
            // Échec : restaurer l'état visuel original
            this.restoreOriginalVisualState();

            // Essayer de traiter le Turbo Stream (pour les messages flash)
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('turbo-stream')) {
              const turboStreamText = await response.text();
              if (turboStreamText) {
                // Utiliser Turbo pour traiter le stream (ex: afficher le flash)
                Turbo.renderStreamMessage(turboStreamText);
              }
            } else {
              // Sinon, essayer de lire le JSON
              try {
                const data = await response.json();
                console.error('Failed to move appointment:', data.message || data.error);
              } catch {
                console.error('Failed to move appointment: Unknown error');
              }
            }
          }
        })
        .catch(error => {
          console.error('Network error while moving appointment:', error);
          this.restoreOriginalVisualState();
        });
    } else {
      console.error("Form inputs for move action not found.");
      this.restoreOriginalVisualState();
    }
  }

  restoreOriginalVisualState() {
    if (this.originalVisualState) {
      const { parent, nextSibling, elementHTML, elementId } = this.originalVisualState;

      // Vérifier qu'un élément avec cet ID n'existe pas déjà
      const existingElement = document.getElementById(elementId);
      if (existingElement) {
        existingElement.remove();
      }

      // Créer un nouvel élément depuis le HTML sauvegardé
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = elementHTML;
      const newElement = tempDiv.firstElementChild;

      // Restaurer l'élément à sa position visuelle originale
      if (nextSibling && parent.contains(nextSibling)) {
        parent.insertBefore(newElement, nextSibling);
      } else {
        parent.appendChild(newElement);
      }

      console.log('Appointment restored to original visual position');
      this.originalVisualState = null;
    }
  }

  updateHighlightingDuringDrag(event) {
    // Trouver quelle colonne est sous la souris
    const elementUnderMouse = document.elementFromPoint(event.clientX, event.clientY);
    if (!elementUnderMouse) return;

    // Trouver la colonne parente
    const columnContainer = elementUnderMouse.closest('[data-bookable-line]');
    if (!columnContainer) return;

    // Vérifier si la colonne est disponible
    if (columnContainer.dataset.onVacation === 'true') {
      this.lastValidSlotIndex = null; // S'assurer que lastValidSlotIndex est null
      this.clearAllHighlights();
      return;
    }

    // Trouver le conteneur des slots
    const slotsContainer = columnContainer.querySelector('[data-schedule-drag-drop-target="column"]');
    if (!slotsContainer) return;

    // Calculer l'index du slot basé sur la position de la souris
    const rect = slotsContainer.getBoundingClientRect();
    const relativeY = event.clientY - rect.top;
    const slotHeight = 1.2 * 16; // 1.2rem en pixels
    let targetIndex = Math.floor(Math.max(0, relativeY) / slotHeight);

    // Vérifier que l'index existe
    const timeSlots = Array.from(slotsContainer.querySelectorAll('.time-slot'));
    if (timeSlots.length === 0) return;

    targetIndex = Math.max(0, Math.min(targetIndex, timeSlots.length - 1));

    // Vérifier qu'il existe un slot avec cet index
    let targetSlot = timeSlots.find(s => parseInt(s.dataset.slotIndex) === targetIndex);
    if (!targetSlot) {
      targetSlot = timeSlots[targetIndex];
      if (targetSlot) {
        targetIndex = parseInt(targetSlot.dataset.slotIndex);
      }
    }

    if (!targetSlot || isNaN(targetIndex)) return;

    this.clearAllHighlights();

    const slotsNeeded = Math.ceil(this.draggedDuration / 5);
    const bookableId = slotsContainer.dataset.bookableId;

    const canDrop = this.canDropAtPosition(slotsContainer, targetIndex, slotsNeeded, bookableId);

    if (canDrop) {
      // Mettre à jour lastValidSlotIndex pour onEnd SEULEMENT si le drop est autorisé
      this.lastValidSlotIndex = targetIndex;
      this.highlightSlots(slotsContainer, targetIndex, slotsNeeded, 'bg-green-100 border-green-300');
    } else {
      // Effacer l'index valide si le drop n'est pas autorisé
      this.lastValidSlotIndex = null;
      this.highlightSlots(slotsContainer, targetIndex, slotsNeeded, 'bg-red-100 border-red-300');
    }
  }
}
